/**
 * ImageEditResults Component
 *
 * Multi-Image Preview Grid (1-4 Bilder)
 * @accessibility WCAG 2.1 AA konform
 */

import { Download, Check } from 'lucide-react';
import { Button } from '@/components/v2/ui';
import type { Image } from '@/types';

interface ImageEditResultsProps {
  images: Image[];
  onDownload?: (image: Image) => void;
  onSelect?: (image: Image) => void;
}

export function ImageEditResults({ images, onDownload, onSelect }: ImageEditResultsProps) {
  if (images.length === 0) {
    return null;
  }

  return (
    <div className="image-edit-results p-4 bg-white dark:bg-neutral-900 border-t border-neutral-200 dark:border-neutral-800">
      <h3 className="text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-3">
        Generierte Bilder ({images.length})
      </h3>

      <div className="flex gap-4 overflow-x-auto pb-2">
        {images.map((image, idx) => (
          <div
            key={image.id}
            className="image-edit-result-item relative flex-shrink-0 w-48 h-48 border border-neutral-300 dark:border-neutral-700 rounded overflow-hidden hover:ring-2 hover:ring-primary-500 dark:hover:ring-primary-400 transition-all group"
          >
            <img
              src={image.image_url}
              alt={`Generiertes Bild ${idx + 1}`}
              className="w-full h-full object-cover"
            />

            {/* Actions Overlay */}
            <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all flex items-center justify-center gap-2 opacity-0 group-hover:opacity-100">
              {onDownload && (
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => onDownload(image)}
                  aria-label={`Bild ${idx + 1} herunterladen`}
                >
                  <Download className="h-4 w-4" />
                </Button>
              )}
              {onSelect && (
                <Button
                  variant="primary"
                  size="sm"
                  onClick={() => onSelect(image)}
                  aria-label={`Bild ${idx + 1} verwenden`}
                >
                  <Check className="h-4 w-4" />
                </Button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
